import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { DealChip } from "~/components/ui/DealChip";
import { cn, formatPrice, formatKm, type DealRating, type ReportingCurrency } from "~/lib/utils";
import { useT } from "~/lib/i18n";

export const MAX_COMPARE = 4;

const PANEL_WIDTH_KEY = "ms_compare_panel_width";
const PANEL_WIDTH_DEFAULT = 640;
const PANEL_WIDTH_MIN = 400;
const PANEL_WIDTH_MAX_RATIO = 0.92;

function readStoredPanelWidth(): number {
  if (typeof window === "undefined") return PANEL_WIDTH_DEFAULT;
  const raw = localStorage.getItem(PANEL_WIDTH_KEY);
  const n = raw ? Number(raw) : NaN;
  if (!Number.isFinite(n)) return clampPanelWidth(PANEL_WIDTH_DEFAULT);
  return clampPanelWidth(n);
}

function clampPanelWidth(w: number): number {
  if (typeof window === "undefined") return w;
  const max = Math.floor(window.innerWidth * PANEL_WIDTH_MAX_RATIO);
  return Math.min(max, Math.max(PANEL_WIDTH_MIN, Math.round(w)));
}

export interface CompareDeal {
  id: string;
  canonicalUrl: string;
  source: string;
  currentTitle: string | null;
  currentPriceEur: number | null;
  currentPriceUsd?: number | null;
  currentPriceChf?: number | null;
  currentKm: number | null;
  currentYear: number | null;
  currentCountry: string | null;
  fairPriceEur: number | null;
  dealRating: string | null;
  dealScore: number | null;
  dealSavingsEur: number | null;
  thumbnailUrl?: string | null;
}

interface CompareContextValue {
  items: CompareDeal[];
  panelOpen: boolean;
  panelWidth: number;
  isInCompare: (id: string) => boolean;
  toggle: (deal: CompareDeal) => void;
  remove: (id: string) => void;
  clear: () => void;
  setPanelOpen: (open: boolean) => void;
  setPanelWidth: (w: number) => void;
}

const CompareContext = createContext<CompareContextValue | null>(null);

export function CompareProvider({
  children,
  currency = "EUR",
}: {
  children: React.ReactNode;
  currency?: ReportingCurrency;
}): React.JSX.Element {
  const [items, setItems] = useState<CompareDeal[]>([]);
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelWidth, setPanelWidthState] = useState(readStoredPanelWidth);

  const setPanelWidth = useCallback((w: number) => {
    const next = clampPanelWidth(w);
    setPanelWidthState(next);
    try {
      localStorage.setItem(PANEL_WIDTH_KEY, String(next));
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    const onResize = () => setPanelWidthState((w) => clampPanelWidth(w));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const isInCompare = useCallback((id: string) => items.some((d) => d.id === id), [items]);

  const toggle = useCallback((deal: CompareDeal) => {
    setItems((prev) => {
      const idx = prev.findIndex((d) => d.id === deal.id);
      if (idx >= 0) {
        const next = prev.filter((d) => d.id !== deal.id);
        if (next.length === 0) setPanelOpen(false);
        return next;
      }
      if (prev.length >= MAX_COMPARE) return prev;
      setPanelOpen(true);
      return [...prev, deal];
    });
  }, []);

  const remove = useCallback((id: string) => {
    setItems((prev) => {
      const next = prev.filter((d) => d.id !== id);
      if (next.length === 0) setPanelOpen(false);
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    setItems([]);
    setPanelOpen(false);
  }, []);

  const value = useMemo(
    () => ({ items, panelOpen, panelWidth, isInCompare, toggle, remove, clear, setPanelOpen, setPanelWidth }),
    [items, panelOpen, panelWidth, isInCompare, toggle, remove, clear, setPanelWidth],
  );

  return (
    <CompareContext.Provider value={value}>
      {children}
      <CompareTray />
      <ComparePanel currency={currency} />
    </CompareContext.Provider>
  );
}

export function useCompare(): CompareContextValue {
  const ctx = useContext(CompareContext);
  if (!ctx) throw new Error("useCompare must be used within CompareProvider");
  return ctx;
}

export function useCompareOptional(): CompareContextValue | null {
  return useContext(CompareContext);
}

function CompareTray(): React.JSX.Element | null {
  const { t } = useT();
  const { items, panelOpen, panelWidth, setPanelOpen, clear, remove, isInCompare } = useCompare();

  if (items.length === 0) return null;

  return (
    <div
      className={cn(
        "fixed bottom-0 left-0 right-0 z-40 border-t border-[var(--divider-strong)]",
        "bg-[var(--surface-card)]/95 backdrop-blur-md shadow-[0_-8px_32px_-8px_rgba(0,0,0,0.15)]",
        panelOpen && "lg:right-0",
      )}
      style={panelOpen ? { right: panelWidth } : undefined}
    >
      <div className="w-full max-w-[1440px] mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
        <span className="text-[11px] mono uppercase tracking-wider text-ink-tertiary shrink-0 hidden sm:inline">
          {t("web.compare.trayLabel")}
        </span>
        <div className="flex items-center gap-2 flex-1 min-w-0 overflow-x-auto pb-0.5">
          {items.map((d) => (
            <button
              key={d.id}
              type="button"
              onClick={() => setPanelOpen(true)}
              className={cn(
                "shrink-0 flex items-center gap-2 pl-1 pr-2 py-1 rounded-lg border transition-colors",
                isInCompare(d.id)
                  ? "border-accent bg-accent/10"
                  : "border-[var(--divider-weak)] hover:border-[var(--divider-strong)]",
              )}
            >
              <span className="relative h-10 w-14 rounded overflow-hidden bg-[var(--surface-sunken)]">
                {d.thumbnailUrl ? (
                  <img src={d.thumbnailUrl} alt="" className="h-full w-full object-cover" />
                ) : (
                  <span className="flex h-full w-full items-center justify-center text-[10px] text-ink-disabled">—</span>
                )}
              </span>
              <span className="text-[11px] text-ink-primary max-w-[100px] truncate hidden sm:block">
                {formatPrice(d.currentPriceEur, { compact: true, locale: "fr-FR", currency: "EUR" })}
              </span>
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  remove(d.id);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.stopPropagation();
                    remove(d.id);
                  }
                }}
                className="ml-0.5 p-0.5 text-ink-disabled hover:text-red-500"
                aria-label={t("web.compare.remove")}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </span>
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-[11px] mono text-ink-tertiary">
            {items.length}/{MAX_COMPARE}
          </span>
          <button
            type="button"
            onClick={() => setPanelOpen(!panelOpen)}
            className="h-9 px-3 text-[12px] font-medium rounded-lg bg-accent text-[var(--ink-on-accent)] hover:bg-[var(--accent-hover)] transition-colors"
          >
            {panelOpen ? t("web.compare.hidePanel") : t("web.compare.showPanel")}
          </button>
          <button
            type="button"
            onClick={clear}
            className="h-9 px-3 text-[12px] text-ink-secondary hover:text-ink-primary transition-colors"
          >
            {t("web.compare.clear")}
          </button>
        </div>
      </div>
    </div>
  );
}

function PanelResizeHandle(): React.JSX.Element {
  const { panelWidth, setPanelWidth } = useCompare();
  const { t } = useT();
  const dragRef = useRef<{ startX: number; startW: number } | null>(null);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragRef.current) return;
      const delta = dragRef.current.startX - e.clientX;
      setPanelWidth(dragRef.current.startW + delta);
    };
    const onUp = () => {
      dragRef.current = null;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [setPanelWidth]);

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={t("web.compare.resize")}
      title={t("web.compare.resize")}
      className={cn(
        "absolute left-0 top-0 bottom-0 z-10 w-3 -translate-x-1/2",
        "flex items-center justify-center cursor-col-resize group",
      )}
      onMouseDown={(e) => {
        e.preventDefault();
        dragRef.current = { startX: e.clientX, startW: panelWidth };
        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";
      }}
      onDoubleClick={() => setPanelWidth(PANEL_WIDTH_DEFAULT)}
    >
      <div
        className={cn(
          "h-16 w-1 rounded-full transition-colors",
          "bg-[var(--divider-strong)] group-hover:bg-accent group-active:bg-accent",
        )}
      />
    </div>
  );
}

interface CompareHighlights {
  bestPrice: string | null;
  lowestKm: string | null;
  bestDeal: string | null;
}

function computeHighlights(items: CompareDeal[]): CompareHighlights {
  if (items.length < 2) return { bestPrice: null, lowestKm: null, bestDeal: null };
  let bestPrice: CompareDeal | null = null;
  let lowestKm: CompareDeal | null = null;
  let bestDeal: CompareDeal | null = null;

  for (const d of items) {
    if (d.currentPriceEur != null) {
      if (!bestPrice || d.currentPriceEur < (bestPrice.currentPriceEur ?? Infinity)) bestPrice = d;
    }
    if (d.currentKm != null) {
      if (!lowestKm || d.currentKm < (lowestKm.currentKm ?? Infinity)) lowestKm = d;
    }
    if (d.dealScore != null) {
      if (!bestDeal || (d.dealScore ?? 0) > (bestDeal.dealScore ?? 0)) bestDeal = d;
    }
  }
  return {
    bestPrice: bestPrice?.id ?? null,
    lowestKm: lowestKm?.id ?? null,
    bestDeal: bestDeal?.id ?? null,
  };
}

function ComparePanel({ currency }: { currency: ReportingCurrency }): React.JSX.Element | null {
  const { t, numberLocale } = useT();
  const { items, panelOpen, panelWidth, setPanelOpen, remove, clear } = useCompare();
  const highlights = useMemo(() => computeHighlights(items), [items]);

  if (!panelOpen || items.length === 0) return null;

  return (
    <>
      <button
        type="button"
        className="fixed inset-0 z-40 bg-black/20 lg:hidden"
        aria-label={t("web.compare.hidePanel")}
        onClick={() => setPanelOpen(false)}
      />
      <aside
        className={cn(
          "fixed top-12 right-0 z-50 flex flex-col",
          "w-full lg:w-[var(--compare-panel-w)] h-[calc(100dvh-3rem)]",
          "border-l border-[var(--divider-strong)] bg-[var(--surface-card)] shadow-2xl",
          "lg:max-w-[92vw]",
        )}
        style={{ "--compare-panel-w": `${panelWidth}px` } as React.CSSProperties}
      >
        <div className="hidden lg:block">
          <PanelResizeHandle />
        </div>
        <header className="shrink-0 flex items-center justify-between gap-3 px-4 py-3 border-b border-[var(--divider-weak)]">
          <div>
            <h2 className="text-sm font-semibold text-ink-primary">{t("web.compare.panelTitle")}</h2>
            <p className="text-[11px] text-ink-tertiary mt-0.5">{t("web.compare.panelHint")}</p>
          </div>
          <button
            type="button"
            onClick={() => setPanelOpen(false)}
            className="h-8 w-8 flex items-center justify-center rounded-md text-ink-tertiary hover:bg-[var(--surface-hover)]"
            aria-label={t("web.compare.hidePanel")}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </header>
        <div className="flex-1 min-h-0 overflow-hidden">
          <div className="flex h-full w-full divide-x divide-[var(--divider-weak)]">
            {items.map((deal) => (
              <CompareColumn
                key={deal.id}
                deal={deal}
                currency={currency}
                numberLocale={numberLocale}
                onRemove={() => remove(deal.id)}
                highlights={highlights}
              />
            ))}
          </div>
        </div>
        <footer className="shrink-0 px-4 py-3 border-t border-[var(--divider-weak)] flex justify-end">
          <button
            type="button"
            onClick={clear}
            className="text-[12px] text-ink-tertiary hover:text-ink-primary"
          >
            {t("web.compare.clear")}
          </button>
        </footer>
      </aside>
    </>
  );
}

function CompareColumn({
  deal,
  currency,
  numberLocale,
  onRemove,
  highlights,
}: {
  deal: CompareDeal;
  currency: ReportingCurrency;
  numberLocale: string;
  onRemove: () => void;
  highlights: CompareHighlights;
}): React.JSX.Element {
  const { t } = useT();
  const rating = (deal.dealRating ?? "unknown") as DealRating;
  const price =
    currency === "USD"
      ? deal.currentPriceUsd
      : currency === "CHF"
        ? deal.currentPriceChf
        : deal.currentPriceEur;

  const isBestPrice = highlights.bestPrice === deal.id;
  const isLowestKm = highlights.lowestKm === deal.id;
  const isBestDeal = highlights.bestDeal === deal.id;

  return (
    <div className="flex-1 min-w-0 flex flex-col h-full overflow-y-auto">
      <div className="relative aspect-[16/10] min-h-[140px] bg-[var(--surface-sunken)] shrink-0">
        {deal.thumbnailUrl ? (
          <img src={deal.thumbnailUrl} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-ink-disabled text-xs">—</div>
        )}
        <div className="absolute top-2 left-2 flex items-center gap-1">
          <DealChip rating={rating} size="sm" />
          {isBestDeal && (
            <span className="h-5 px-1.5 rounded bg-deal-super/90 text-white text-[9px] font-bold flex items-center backdrop-blur-sm">
              ★ Best
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={onRemove}
          className="absolute top-2 right-2 h-7 w-7 rounded-md bg-black/50 text-white flex items-center justify-center hover:bg-black/70"
          aria-label={t("web.compare.remove")}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div className="p-4 flex flex-col gap-2.5 flex-1">
        <h3 className="text-[14px] font-medium text-ink-primary leading-snug line-clamp-4">
          {deal.currentTitle ?? "—"}
        </h3>
        <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-[12px]">
          <dt className="text-ink-tertiary">{t("web.list.col.price")}</dt>
          <dd className={cn(
            "mono font-semibold text-right",
            isBestPrice ? "text-deal-super" : "text-ink-primary",
          )}>
            {formatPrice(price, { locale: numberLocale, currency })}
            {isBestPrice && <span className="ml-1 text-[9px] font-bold align-super">↓</span>}
          </dd>
          <dt className="text-ink-tertiary">{t("web.list.col.km")}</dt>
          <dd className={cn(
            "mono text-right",
            isLowestKm ? "text-deal-super font-medium" : "text-ink-secondary",
          )}>
            {deal.currentKm != null ? formatKm(deal.currentKm, numberLocale) : "—"}
            {isLowestKm && <span className="ml-1 text-[9px] font-bold align-super">↓</span>}
          </dd>
          <dt className="text-ink-tertiary">{t("web.list.col.year")}</dt>
          <dd className="mono text-ink-secondary text-right">{deal.currentYear ?? "—"}</dd>
          {deal.fairPriceEur != null && (
            <>
              <dt className="text-ink-tertiary">{t("web.list.col.fairPrice")}</dt>
              <dd className="mono text-ink-secondary text-right">
                {formatPrice(deal.fairPriceEur, { locale: numberLocale, currency: "EUR" })}
              </dd>
            </>
          )}
          {deal.dealSavingsEur != null && deal.dealSavingsEur > 0 && (
            <>
              <dt className="text-ink-tertiary">{t("web.list.col.savings")}</dt>
              <dd className="mono text-deal-super font-medium text-right">
                −{formatPrice(deal.dealSavingsEur, { compact: true, locale: numberLocale, currency: "EUR" })}
              </dd>
            </>
          )}
        </dl>
        <div className="mt-auto pt-2 flex flex-col gap-1.5">
          <Link
            to={`/listing/${deal.id}`}
            className="h-9 flex items-center justify-center text-[12px] font-medium rounded-lg border border-[var(--divider-strong)] hover:bg-[var(--surface-hover)] transition-colors"
          >
            {t("web.compare.openListing")}
          </Link>
          <a
            href={deal.canonicalUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="h-8 flex items-center justify-center text-[11px] text-accent hover:underline"
          >
            {t("web.compare.viewSource")}
          </a>
        </div>
      </div>
    </div>
  );
}

/** Scatter / list hover card with photo */
export function CompareHoverCard({
  deal,
  left,
  top,
  hint,
}: {
  deal: CompareDeal;
  left: number;
  top: number;
  hint?: string;
}): React.JSX.Element {
  const { numberLocale } = useT();
  const rating = (deal.dealRating ?? "unknown") as DealRating;

  return (
    <div
      className="absolute pointer-events-none z-30"
      style={{ left, top: top - 8, transform: "translate(-50%, -100%)" }}
    >
      <div className="bg-[var(--surface-card)] border border-[var(--divider-strong)] rounded-lg shadow-xl overflow-hidden w-[220px]">
        {deal.thumbnailUrl && (
          <img src={deal.thumbnailUrl} alt="" className="w-full h-[100px] object-cover" />
        )}
        <div className="px-3 py-2">
          <div className="flex items-start gap-2">
            <DealChip rating={rating} size="sm" />
            <p className="text-[11px] font-medium text-ink-primary line-clamp-2 leading-tight flex-1">
              {deal.currentTitle ?? "—"}
            </p>
          </div>
          <div className="mt-1.5 flex items-center gap-2 text-[10px] mono text-ink-secondary">
            <span>{formatPrice(deal.currentPriceEur, { locale: numberLocale, currency: "EUR" })}</span>
            {deal.currentKm != null && (
              <>
                <span className="opacity-40">·</span>
                <span>{formatKm(deal.currentKm, numberLocale)}</span>
              </>
            )}
          </div>
          {hint && (
            <p className="mt-1.5 text-[10px] text-accent">{hint}</p>
          )}
        </div>
      </div>
    </div>
  );
}

export function CompareToggleButton({
  deal,
  className,
  size = "sm",
}: {
  deal: CompareDeal;
  className?: string;
  size?: "sm" | "md";
}): React.JSX.Element | null {
  const { t } = useT();
  const compare = useCompareOptional();
  if (!compare) return null;

  const active = compare.isInCompare(deal.id);
  const atMax = compare.items.length >= MAX_COMPARE && !active;

  return (
    <button
      type="button"
      title={active ? t("web.compare.removeFromList") : t("web.compare.addToList")}
      disabled={atMax}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        compare.toggle(deal);
      }}
      className={cn(
        "flex items-center justify-center rounded-md transition-all active:scale-[0.92]",
        size === "sm" ? "h-8 w-8" : "h-9 px-3 gap-1.5 text-[12px] font-medium",
        active
          ? "bg-accent text-[var(--ink-on-accent)] shadow-sm"
          : "bg-white/90 dark:bg-black/50 text-ink-secondary hover:text-accent border border-[var(--divider-weak)] backdrop-blur-sm",
        atMax && "opacity-40 cursor-not-allowed",
        className,
      )}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 5v14M5 12h14" />
      </svg>
      {size === "md" && (
        <span>{active ? t("web.compare.inList") : t("web.compare.add")}</span>
      )}
    </button>
  );
}
